import { db } from '../firebaseFunctions/config';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SUBSCRIPTION_PLANS = {
  free: {
    id: 'free',
    name: 'Basic',
    price: 0,
    platformFeePercent: 15,
    eventLimit: 2,      // 2 published events per calendar month
    vendorLimit: null
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 249,
    yearlyPrice: 2539,          // 15% off annual ($211.58/mo effective)
    platformFeePercent: 15,
    eventLimit: null,           // unlimited
    vendorLimit: null,          // unlimited
    ticketingFeePercent: 3.7,
    ticketingFeeFixed: 1.79,    // matches Eventbrite standard rate
    seats: 3                    // owner + 2 contributors
  }
};

// Payment Links from Stripe Dashboard
// Set these in your .env file
const PAYMENT_LINKS = {
  pro: {
    monthly: process.env.REACT_APP_STRIPE_PRO_MONTHLY_LINK || 'https://buy.stripe.com/aFa00j7o5cIlacw5gI2oE05',
    yearly: process.env.REACT_APP_STRIPE_PRO_YEARLY_LINK || 'https://buy.stripe.com/6oU9ATfUB23H4SccJa2oE04'
  }
};

export const subscriptionService = {
  async getSubscriptionStatus(userId) {
    if (!userId) return { hasActiveSubscription: false, status: 'none' };

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (!userDoc.exists()) {
        return { hasActiveSubscription: false, status: 'none' };
      }

      const userData = userDoc.data();
      const subscription = userData.subscription || {};

      console.log('[SubscriptionService] User ID:', userId);
      console.log('[SubscriptionService] userData.hasActiveSubscription:', userData.hasActiveSubscription, 'type:', typeof userData.hasActiveSubscription);
      console.log('[SubscriptionService] Full userData keys:', Object.keys(userData));
      console.log('[SubscriptionService] subscription object:', userData.subscription);

      // Trust the top-level hasActiveSubscription field as source of truth
      // This handles cases where Stripe webhook didn't update currentPeriodEnd
      // Handle both boolean true and string "true" for robustness
      if (userData.hasActiveSubscription === true || userData.hasActiveSubscription === 'true') {
        console.log('[SubscriptionService] Returning hasActiveSubscription: TRUE (from top-level field)');
        return {
          hasActiveSubscription: true,
          status: subscription.status || 'active',
          plan: subscription.plan || userData.subscriptionTier || 'basic',
          currentPeriodEnd: subscription.currentPeriodEnd?.toDate?.() || null,
          stripeSubscriptionId: subscription.stripeSubscriptionId,
          stripeCustomerId: subscription.stripeCustomerId
        };
      }

      if (!subscription.status) {
        // No subscription record — user hasn't selected a plan yet
        return { hasActiveSubscription: false, status: 'none', plan: null };
      }

      const now = new Date();
      const isActive = subscription.status === 'active';
      const currentPeriodEnd = subscription.currentPeriodEnd?.toDate?.() || new Date(subscription.currentPeriodEnd);
      const isExpired = currentPeriodEnd < now;

      if (isActive && !isExpired) {
        return {
          hasActiveSubscription: true,
          status: subscription.status,
          plan: subscription.plan || 'basic',
          currentPeriodEnd: currentPeriodEnd,
          stripeSubscriptionId: subscription.stripeSubscriptionId,
          stripeCustomerId: subscription.stripeCustomerId
        };
      }

      return {
        hasActiveSubscription: false,
        status: isExpired ? 'expired' : subscription.status,
        plan: subscription.plan
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { hasActiveSubscription: false, status: 'error' };
    }
  },

  /**
   * Get the Payment Link URL for a given plan and billing cycle.
   * Appends client_reference_id for webhook to identify the user.
   */
  getPaymentLinkUrl(userId, planId = 'pro', billingCycle = 'monthly') {
    const plan = SUBSCRIPTION_PLANS[planId];
    if (!plan || plan.price === 0) {
      throw new Error('Invalid plan selected for payment');
    }

    const paymentLink = PAYMENT_LINKS[planId]?.[billingCycle];
    if (!paymentLink) {
      throw new Error(`Payment link not configured for ${planId} ${billingCycle}`);
    }

    // Append client_reference_id so webhook can identify the user
    const url = new URL(paymentLink);
    url.searchParams.set('client_reference_id', userId);

    return url.toString();
  },

  /**
   * Redirect to Stripe Payment Link for subscription checkout.
   * The webhook will handle updating Firestore when payment completes.
   */
  redirectToPaymentLink(userId, planId, billingCycle = 'monthly') {
    const url = this.getPaymentLinkUrl(userId, planId, billingCycle);
    window.location.href = url;
  },

  async activateSubscription(userId, subscriptionData) {
    try {
      const userRef = doc(db, 'users', userId);

      // Build subscription object, only including defined values to avoid Firestore errors
      const subscription = {
        status: subscriptionData.status || 'active',
        plan: subscriptionData.plan || 'basic',
        currentPeriodStart: subscriptionData.currentPeriodStart || serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Only include optional fields if they have values (Firestore rejects undefined)
      if (subscriptionData.stripeSubscriptionId !== undefined) {
        subscription.stripeSubscriptionId = subscriptionData.stripeSubscriptionId;
      }
      if (subscriptionData.stripeCustomerId !== undefined) {
        subscription.stripeCustomerId = subscriptionData.stripeCustomerId;
      }
      if (subscriptionData.currentPeriodEnd !== undefined) {
        subscription.currentPeriodEnd = subscriptionData.currentPeriodEnd;
      }

      await updateDoc(userRef, {
        subscription,
        subscriptionTier: subscriptionData.plan || 'basic',
        hasActiveSubscription: true
      });

      return { success: true };
    } catch (error) {
      console.error('Error activating subscription:', error);
      throw error;
    }
  },

  async cancelSubscription(userId) {
    try {
      const functions = getFunctions();
      const cancelSub = httpsCallable(functions, 'cancelSubscription');

      const result = await cancelSub({ userId });

      return result.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },

  async pauseSubscription(userId, durationMonths) {
    try {
      const functions = getFunctions();
      const pauseSub = httpsCallable(functions, 'pauseSubscription');

      const result = await pauseSub({ userId, durationMonths: Number(durationMonths) });

      return result.data;
    } catch (error) {
      console.error('Error pausing subscription:', error);
      throw error;
    }
  },

  getPlans() {
    return SUBSCRIPTION_PLANS;
  },

  getPlan(planId) {
    return SUBSCRIPTION_PLANS[planId];
  }
};

export default subscriptionService;
